import { useQuery } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import { search, type SearchScope } from '@/services/api/search';
import { useUiStore, useSearchQuery, useSearchFilters } from '@/stores/uiStore';
import { useTreeStore } from '@/stores/treeStore';
import { filtersKey, hasActiveFilters } from '@/types/searchFilters';
import type { SearchScopeKind } from '@/types/domain';

// Resolve the chosen scope kind into concrete container/folder ids taken from the
// active tab. Falls back to 'all' when the required id is missing (e.g. "Ce
// dossier" while no folder is open) so the query is never silently empty — the
// UI also keeps unavailable options disabled, so this is a safety net.
export function resolveScope(
  kind: SearchScopeKind,
  containerId: string | null,
  folderId: string | null,
): SearchScope {
  if (kind === 'container' && containerId) return { scope: 'container', containerId };
  if (kind === 'folder' && folderId) return { scope: 'folder', folderId };
  if (kind === 'favorites') return { scope: 'favorites' };
  return { scope: 'all' };
}

// Défaut contextuel : la portée appliquée tant que l'utilisateur n'a pas choisi
// explicitement dans le menu. Page d'accueil (arbre) → 'all' ; page détail d'un
// projet → 'container' à la racine, 'folder' dès qu'un dossier est ouvert.
export function contextualScope(
  mode: 'tree' | 'detail',
  detailFolderId: string | null,
  activeContainerId: string | null,
): SearchScopeKind {
  if (mode === 'detail') {
    if (detailFolderId) return 'folder';
    if (activeContainerId) return 'container';
  }
  return 'all';
}

// Portée effective résolue en identifiants concrets : le choix explicite de
// l'utilisateur (scopeOverride) s'il existe, sinon le défaut contextuel ci-dessus.
// Partagée par la barre de recherche, la requête et l'affichage des résultats
// pour qu'ils s'accordent toujours. "Le dossier affiché" = le dossier de la page
// détail en mode 'detail', sinon le dossier sélectionné dans l'arbre.
export function useResolvedScope(): SearchScope {
  const override = useUiStore((s) => s.scopeOverride);
  const mode = useTreeStore((s) => s.mode);
  const activeContainerId = useTreeStore((s) => s.activeContainerId);
  const activeFolderId = useTreeStore((s) => s.activeFolderId);
  const detailFolderId = useTreeStore((s) => s.detailFolderId);
  const visibleFolderId = mode === 'detail' ? detailFolderId : activeFolderId;
  const kind = override ?? contextualScope(mode, detailFolderId, activeContainerId);
  return resolveScope(kind, activeContainerId, visibleFolderId);
}

export function useSearch() {
  const query = useSearchQuery();
  const filters = useSearchFilters();
  const scope = useResolvedScope();

  const [debounced] = useDebounce(query, 200);
  // Les filtres sont temporisés comme le terme : chaque frappe dans « De » ou
  // « Objet » relancerait sinon une requête.
  const [debouncedFilters] = useDebounce(filters, 200);
  // Clé dérivée de l'état EFFECTIF des filtres (champs vides omis, valeurs
  // rognées) : deux saisies équivalentes ne provoquent donc pas deux requêtes, et
  // un objet — dont l'identité change à chaque rendu — ne peut pas servir de clé.
  const key = filtersKey(debouncedFilters);
  return useQuery({
    queryKey: [
      'search',
      debounced,
      scope.scope,
      scope.containerId ?? null,
      scope.folderId ?? null,
      key,
    ],
    queryFn: () => search(debounced, scope, debouncedFilters),
    // Un filtre SEUL suffit désormais : « les mails de X en juin » n'a pas de
    // mot-clé. Il faut en revanche l'un des deux, sinon on demanderait l'archive
    // entière.
    enabled: debounced.trim().length > 0 || hasActiveFilters(debouncedFilters),
    staleTime: 10_000,
  });
}
